import Icon from '@ant-design/icons';
import { Button, Popover } from 'antd';
import classNames from 'classnames';
import Image from 'next/image';
import { useMemo } from 'react';

import CorrectSvg from '@/components/Icon/svgs/correct.svg';
import { useTranslation } from '@/i18n/client';
import type { PackageDetailInfo, PackageDetailInfoItem } from '@/types/subscription';
import { EPlanAlias, EPlanInterval } from '@/types/subscription';
import { getSimpleRichTextSegments } from '@/utils/utils';

import styles from './index.less';

export interface PricingCardV2Props {
  className?: string;
  tag?: string;
  detail: PackageDetailInfo;
  using?: boolean;
  isLoading?: boolean;
  onSelectPlan: () => void;
  children?: React.ReactNode; // 自定义内容
}

const renderInfoItemText = (text: string) => {
  const segments = getSimpleRichTextSegments(text);

  return segments.map((item) => {
    const { start, end, isStrong } = item;
    const val = text.substring(start, end).replaceAll('*', '');

    if (isStrong) {
      return (
        <span key={start + val} className="text-primary-sub-color">
          {val}
        </span>
      );
    }

    return <span key={start}>{text.substring(start, end)}</span>;
  });
};

export function CardItem({
  className,
  using = false,
  isLoading,
  detail,
  children,
  tag,
  onSelectPlan
}: PricingCardV2Props): JSX.Element {
  const { alias, price, pricePerMonth, metadata, newRightsList } = detail;
  const { interval } = metadata;
  const isDarkBg = useMemo(() => {
    return alias === EPlanAlias.PLUS || alias === EPlanAlias.PRO;
  }, [alias]);

  const showPopular = alias === EPlanAlias.PLUS && interval === EPlanInterval.ANNUAL;
  const { t } = useTranslation();

  const priceDisplay = (displayPrice: number | undefined) => {
    const _price = displayPrice;

    return _price === 0 ? (
      <span className={styles.price}>{t('menu.subscriptionModal.free')}</span>
    ) : (
      <>
        <span className={styles.dollar}>{'$ '}</span>
        <span className={styles.price}>{_price}</span>
        <span className={styles.mo}>{t('menu.subscriptionModal.perMo')}</span>
      </>
    );
  };

  return (
    <div
      className={classNames(styles.container, 'text-primary-black', className)}
      style={{
        background: isDarkBg ? 'linear-gradient(180deg, #160a0a 0%, #2F2180 100%)' : '#fefdff'
      }}
    >
      <div className={styles.tag}>
        <Image
          alt={'panel title'}
          className="absolute left-1/2 -translate-x-1/2"
          height={28}
          src={
            'https://frontdoorcdn.mindverse.ai/mindos-resource/front-img/crm/202311211700552909321img_v3_025d_0f5c3683-f39d-4929-8ae3-3c142136387g.png'
          }
          width={140}
        />
        <div
          className={classNames('absolute w-[142px] text-center mt-[2px]', 'text-primary-black')}
        >
          {tag}
        </div>
      </div>

      {/* Popular */}
      {showPopular ? (
        <div className="absolute bg-gradient-to-r from-[#FFB959] to-[#7B67EF] w-[110px] h-6 top-[44px] left-1/2 -translate-x-1/2 rounded-[8px] px-[10px] py-[2px] box-border">
          <span className="text-primary-white font-medium text-xs leading-5 whitespace-nowrap">
            {t('menu.subscriptionModal.mostPopular')}
          </span>
        </div>
      ) : null}

      {/* Price monthly */}
      <div
        className={classNames(
          styles.priceInfo,
          isDarkBg ? 'text-primary-white' : 'text-primary-black'
        )}
      >
        {priceDisplay(pricePerMonth)}
      </div>

      {/* Price annually */}
      {interval === EPlanInterval.ANNUAL ? (
        <div className="absolute text-xs leading-5 text-[#acabb3] top-[128px] left-1/2 -translate-x-1/2">
          {t('menu.subscriptionModal.perBilledYr', { price: price })}
        </div>
      ) : null}

      <Button
        className={classNames(styles.button)}
        disabled={using}
        loading={isLoading}
        onClick={() => {
          onSelectPlan();
        }}
      >
        {using ? t('menu.subscriptionModal.currentPlan') : t('menu.subscriptionModal.select')}
      </Button>

      {/* Privileges */}
      <div
        className={classNames(styles.privilege, isDarkBg ? 'text-[white]' : 'text-primary-black')}
      >
        {newRightsList.map((item: PackageDetailInfoItem, index: number) => (
          <div key={index} className={styles.privilegeItem}>
            <Icon className={styles.correctSvg} component={CorrectSvg} />
            <span className={styles.privilegeContent}>
              {item.info && (
                <Popover content={item.info} overlayStyle={{ maxWidth: 300 }}>
                  {renderInfoItemText(item.title)}
                </Popover>
              )}
              {!item.info && renderInfoItemText(item.title)}
            </span>
          </div>
        ))}
        {/* {alias !== EPlanAlias.FREE ? (
          <div className={styles.privilegeItem}>
            <Icon className={styles.correctSvg} component={CorrectSvg} />
            <span
              className={classNames('underline cursor-pointer', styles.privilegeContent)}
              onClick={() => {
                window.open('https://docs.me.bot//more/subscriptions', '_blank');
              }}
            >
              More...
            </span>
          </div>
        ) : null} */}
      </div>
      {children}
    </div>
  );
}
